/**
 * AVT (ambient voice) note-template ↔ form-field mapping.
 *
 * A form declares which note sections its AVT note template emits and which
 * form field each one fills. The section *descriptions* (the instructions
 * pasted into the AVT provider's template) live alongside the form template as
 * a `*.avt-template.md`; this
 * declaration holds only the title → field routing. The section title is the
 * shared contract between the two — keep them in sync.
 *
 * At runtime {@link mapSectionsToFields} joins the emitted sections back onto
 * form fields by matching `title`. Many sections may target the same field
 * (many-to-one); their text is joined in declared order. Section titles stay
 * "data, not named slots" — this declaration is the only place that knows them.
 */
/**
 * Merge incoming dictated text into a field's current value. Appends below the
 * existing content (blank line between) unless replacing or the field is empty.
 * Pure — the review UI calls this per accepted field.
 */
export const mergeFieldText = (current, incoming, mode) => {
    const trimmedCurrent = current.trim();
    if (mode === "replace" || trimmedCurrent.length === 0) {
        return incoming;
    }
    return `${trimmedCurrent}\n\n${incoming}`;
};
const normaliseTitle = (title) => title.trim().toLowerCase();
/**
 * Parse a markdown AVT note into sections for mapping. Splits on ATX
 * headings (`#`..`######`): the heading text becomes the section title and the
 * lines up to the next heading become its text. Anything before the first
 * heading is ignored. Used by the "paste the note" flow so a pasted note maps
 * exactly as the live dictation pipeline would.
 */
export const parseMarkdownSections = (markdown) => {
    const headingRe = /^#{1,6}\s+(.+?)\s*$/;
    const sections = [];
    let current = null;
    const flush = () => {
        if (current) {
            sections.push({
                title: current.title,
                text: current.lines.join("\n").trim(),
            });
        }
    };
    for (const line of markdown.split("\n")) {
        const title = headingRe.exec(line)?.[1]?.trim();
        if (title) {
            flush();
            current = { title, lines: [] };
        }
        else if (current) {
            current.lines.push(line);
        }
    }
    flush();
    return sections;
};
/**
 * Map emitted note sections onto form fields per the declaration.
 *
 * - Many-to-one: multiple sections targeting one field are joined in declared
 *   order, each prefixed with its title for traceability.
 * - One-to-one: a single source is written verbatim.
 * - Empty sections are ignored; nothing is fabricated. Sections with no field
 *   land in `unmatchedSections`; fields with no section land in `unfilledFields`.
 */
export const mapSectionsToFields = (emitted, declaration) => {
    const emittedByTitle = new Map();
    for (const section of emitted) {
        if (section.text.trim().length > 0) {
            emittedByTitle.set(normaliseTitle(section.title), section);
        }
    }
    const fieldOrder = [];
    const declaredByField = new Map();
    for (const section of declaration) {
        const existing = declaredByField.get(section.field);
        if (existing) {
            existing.push(section);
        }
        else {
            declaredByField.set(section.field, [section]);
            fieldOrder.push(section.field);
        }
    }
    const fills = [];
    const unfilledFields = [];
    const usedTitles = new Set();
    for (const field of fieldOrder) {
        const parts = [];
        for (const section of declaredByField.get(field) ?? []) {
            const match = emittedByTitle.get(normaliseTitle(section.title));
            if (match) {
                parts.push({ title: section.title, text: match.text.trim() });
                usedTitles.add(normaliseTitle(section.title));
            }
        }
        if (parts.length === 0) {
            unfilledFields.push(field);
            continue;
        }
        const text = parts
            .map((part) => parts.length === 1 ? part.text : `${part.title}\n${part.text}`)
            .join("\n\n");
        fills.push({ field, text, sources: parts.map((part) => part.title) });
    }
    const unmatchedSections = emitted.filter((section) => section.text.trim().length > 0 &&
        !usedTitles.has(normaliseTitle(section.title)));
    return { fills, unmatchedSections, unfilledFields };
};
//# sourceMappingURL=avtMapping.js.map